import styles from "./collaborator.module.css";
import { useEffect, useState } from "react";
import { useClientInfo } from "frontend/subscriptions";
import { getUnixTimestampUTC } from "shared/util/utils";
import { SyncService } from "./services/syncService";
import { RW } from "shared/datamodel/replicache-wrapper/mutators";
import { useAtomValue } from "jotai";
import { userAtom } from "state-atoms/users-atoms";

const hideCollaboratorDelay = 5000;

interface Position {
  pos: {
    x: number;
    y: number;
  };
  ts: number;
}

export function Collaborator({
  syncService,
  position,
  scale,
  clientID,
}: {
  syncService: SyncService<RW> | null;
  position?: { x: number; y: number };
  scale?: number;
  clientID: string;
}) {
  const { userInfo, lastUpdated, cursor } = useClientInfo(syncService?.getReplicache(), clientID) || {};
  const [lastPos, setLastPos] = useState<Position | null>(null);
  const [shouldShow, setShouldShow] = useState(true);
  const [gotFirstChange, setGotFirstChange] = useState(false);
  const [, setPoke] = useState({});
  const currentUser = useAtomValue(userAtom);
  scale ||= 1;
  let curPos = null;
  if (cursor) {
    curPos = { ...cursor };
    curPos!.x = (curPos!.x + position!.x / (scale! * 10)) * (scale! * 10);
    curPos!.y = (curPos!.y + position!.y / (scale! * 10)) * (scale! * 10);
    //curPos = clientInfo.cursor;
  }

  let elapsed = 0;
  let remaining = 0;
  let visible = false;

  if (curPos) {
    if (!lastPos) {
      //console.debug(`Cursor ${clientID} - got initial position`, curPos);
      setLastPos({ pos: curPos, ts: Date.now() });
    } else {
      if (lastPos.pos.x != curPos.x || lastPos.pos.y != curPos.y) {
        //console.debug(`Cursor ${clientID} - got change to`, curPos);
        setLastPos({ pos: curPos, ts: Date.now() });
        setGotFirstChange(true);
      }
      if (gotFirstChange) {
        elapsed = Date.now() - lastPos.ts;
        remaining = hideCollaboratorDelay - elapsed;
        visible = remaining > 0;
      }
    }
  }

  useEffect(() => {
    if (lastUpdated) {
      const now = getUnixTimestampUTC();
      const minutes = Math.floor((now - lastUpdated) / 1000 / 60);
      setShouldShow(minutes <= 5);
    } else {
      setShouldShow(false);
    }
  }, [lastUpdated]);

  useEffect(() => {
    if (remaining > 0) {
      //console.log(`Cursor ${clientID} - setting timer for ${remaining}ms`);
      const timerID = setTimeout(() => setPoke({}), remaining);
      return () => clearTimeout(timerID);
    }
  });

  /* console.log(
    `Cursor ${clientID} - elapsed ${elapsed}, remaining: ${remaining}, visible: ${visible}`
  );*/

  if (!userInfo || !curPos || !lastUpdated) {
    return null;
  }
  if (currentUser && userInfo.id === currentUser.id) {
    return null;
  }

  const lastActiveMinutesDiff = (getUnixTimestampUTC() - lastUpdated) / 1000 / 60;
  if (lastActiveMinutesDiff > 1) {
    return null;
  }

  return !shouldShow ? null : (
    <div className={styles.collaborator} style={{ opacity: visible ? 1 : 0 }}>
      <div
        className={styles.cursor}
        style={{
          left: curPos.x,
          top: curPos.y,
          overflow: "auto",
        }}
      >
        <div className={styles.pointer}>
          <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M2.24946 1.25163C1.96599 1.15714 1.65348 1.23092 1.4422 1.4422C1.23092 1.65348 1.15714 1.96599 1.25163 2.24946L7.14174 19.9198C7.24264 20.2225 7.51593 20.4346 7.83419 20.4572C8.15245 20.4798 8.453 20.3085 8.59569 20.0231L12.4048 12.4048L20.0231 8.59569C20.3085 8.453 20.4798 8.15245 20.4572 7.83419C20.4346 7.51593 20.2225 7.24264 19.9198 7.14174L2.24946 1.25163Z"
              fill={userInfo.color}
              stroke="white"
              strokeWidth="1.57771"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <div
          className={styles.userinfo}
          style={{
            backgroundColor: userInfo.color,
            color: userInfo.textColor,
          }}
        >
          {userInfo.name}
        </div>
      </div>
    </div>
  );
}
